/**
 * What a Deepfake reply thread looks like, decided in ONE place from what the API sends (pure, no React).
 *
 * HumanFirewall's Deepfake Simulation is the authorised, consented awareness exercise an organisation
 * runs against its OWN employees. The inbox row's chip, what the area under the conversation offers,
 * the verdict on the decision card, the asked-for-and-received list, the order of a timeline and the
 * script of a reply are rules, so they live here and are tested here (`__tests__/replies-view.spec.ts`)
 * rather than in the JSX of four components that must agree.
 */
import { parseApiDate } from '@/lib/time/parse-api-date'
import {
  DEEPFAKE_VERIFIED_INTENTS,
  DEEPFAKE_VERIFIED_STOP_REASONS,
  type DeepfakeAskedAndReceived,
  type DeepfakeReplyThreadItem,
  type DeepfakeThreadMessage,
} from './replies-schemas'

// ── what is true of a thread ─────────────────────────────────────────────────

/** The person resisted: they verified another way or reported it (the API's `verified` filter says the same). */
export function isVerifiedThread(thread: Pick<DeepfakeReplyThreadItem, 'stoppedReason' | 'latestIntent'>): boolean {
  return (
    (thread.stoppedReason !== null && (DEEPFAKE_VERIFIED_STOP_REASONS as readonly string[]).includes(thread.stoppedReason)) ||
    (thread.latestIntent !== null && (DEEPFAKE_VERIFIED_INTENTS as readonly string[]).includes(thread.latestIntent))
  )
}

/** Nothing human has been said: only auto-responses (out of office, bounces) are in the thread. */
export function isAutomaticOnly(thread: Pick<DeepfakeReplyThreadItem, 'replyCount' | 'messageCount'>): boolean {
  return thread.messageCount > 0 && thread.replyCount === 0
}

// ── the chip on an inbox row ─────────────────────────────────────────────────

export type ThreadChipKey =
  | 'compromised'
  | 'verified'
  | 'automatic'
  | 'senderMismatch'
  | 'possible'
  | 'agentPending'
  | 'draftWaiting'
  | 'needsReply'
  | 'answeredAgent'
  | 'answered'
  | 'stopped'
  | 'closed'
  | 'waiting'

/** `compromised` is the one tone drawn by hand (solid red); the rest are Badge variants. */
export type ThreadChipTone = 'warning' | 'success' | 'info' | 'secondary' | 'compromised'

export interface ThreadChip {
  /** The catalogue key under `deepfake.replies.chip.`. */
  key: ThreadChipKey
  tone: ThreadChipTone
}

/**
 * The one chip a thread wears, strongest fact first: a compromise, a verification, an automatic reply,
 * a disclosure the AI is not sure of, a draft or the agent waiting, a reply nobody has answered, and then
 * how a finished thread ended.
 */
export function threadChip(thread: DeepfakeReplyThreadItem): ThreadChip {
  if (thread.compromised) return { key: 'compromised', tone: 'compromised' }
  if (isVerifiedThread(thread)) return { key: 'verified', tone: 'success' }
  if (isAutomaticOnly(thread)) return { key: 'automatic', tone: 'secondary' }
  // Held because it came from another address than the invited person's: nothing was disclosed by that person.
  if (thread.senderMismatch) return { key: 'senderMismatch', tone: 'warning' }
  if (thread.needsReview) return { key: 'possible', tone: 'warning' }
  if (thread.status === 'AwaitingAgent') return { key: 'agentPending', tone: 'info' }
  if (thread.status === 'AwaitingApproval' || thread.status === 'AgentDrafted') return { key: 'draftWaiting', tone: 'warning' }
  if (thread.needsReply) return { key: 'needsReply', tone: 'warning' }
  if (thread.status === 'Stopped') return { key: 'stopped', tone: 'secondary' }
  if (thread.status === 'Closed') return { key: 'closed', tone: 'secondary' }
  if (thread.status === 'Replied') {
    return thread.lastMessageRole === 'agent' ? { key: 'answeredAgent', tone: 'info' } : { key: 'answered', tone: 'info' }
  }
  return { key: 'waiting', tone: 'secondary' }
}

// ── what the action area offers ──────────────────────────────────────────────

export type ActionState =
  | 'compromised'
  | 'verified'
  | 'automatic'
  | 'stopped'
  | 'closed'
  | 'answered'
  /** The admin can still answer: the mode decides which card. */
  | 'compose'

export function actionStateOf(thread: DeepfakeReplyThreadItem): ActionState {
  if (thread.compromised) return 'compromised'
  if (isVerifiedThread(thread)) return 'verified'
  if (isAutomaticOnly(thread)) return 'automatic'
  if (thread.status === 'Stopped') return 'stopped'
  if (thread.status === 'Closed') return 'closed'
  if (thread.status === 'Replied' && !thread.needsReply) return 'answered'
  return 'compose'
}

export type Verdict = 'compromised' | 'notCompromised' | 'notCounted' | 'senderMismatch' | 'possible' | 'notYet'

/** The verdict on the decision card (the admin's override is a separate call and a fresh read). */
export function verdictOf(thread: DeepfakeReplyThreadItem): Verdict {
  if (thread.compromised) return 'compromised'
  if (isVerifiedThread(thread)) return 'notCompromised'
  if (isAutomaticOnly(thread)) return 'notCounted'
  if (thread.senderMismatch) return 'senderMismatch'
  if (thread.needsReview) return 'possible'
  return 'notYet'
}

// ── asked for and received ───────────────────────────────────────────────────

export interface KindRow {
  kind: string
  received: boolean
}

/** What the exercise asked for (in order, each Received or Not yet), then any kind received that was not asked for. */
export function kindRows(input: DeepfakeAskedAndReceived): KindRow[] {
  const received = new Set(input.received)
  const rows: KindRow[] = []
  const seen = new Set<string>()
  for (const kind of input.asked) {
    if (seen.has(kind)) continue
    seen.add(kind)
    rows.push({ kind, received: received.has(kind) })
  }
  for (const kind of input.received) {
    if (seen.has(kind)) continue
    seen.add(kind)
    rows.push({ kind, received: true })
  }
  return rows
}

// ── time order and tone ──────────────────────────────────────────────────────

/** A copy of `items` in the order they happened (stable: equal instants keep the order they came in). */
export function chronological<T>(items: readonly T[], at: (item: T) => string): T[] {
  const time = (item: T) => parseApiDate(at(item))?.getTime() ?? 0
  return items
    .map((item, index) => ({ item, index, time: time(item) }))
    .sort((a, b) => a.time - b.time || a.index - b.index)
    .map((entry) => entry.item)
}

export type TimelineTone = 'bad' | 'good' | 'warn' | 'info'

const BAD_EVENTS = new Set(['Compromised', 'AutoPaused', 'InviteFailed'])
const WARN_EVENTS = new Set(['ReplyReceived', 'ClassificationSkipped', 'DraftExpired'])

/** The colour of a timeline dot. An event this build does not know is plain information. */
export function timelineTone(type: string): TimelineTone {
  if (BAD_EVENTS.has(type)) return 'bad'
  if (type === 'Reported') return 'good'
  if (WARN_EVENTS.has(type)) return 'warn'
  return 'info'
}

// ── the script of a reply ────────────────────────────────────────────────────

/** The redactor's markers ([CARD], [REDACTED SECRET]...) are our words, not the employee's. */
const MARKER = /\[[A-Z][A-Z ]{1,30}\]/gu
const ARABIC_LETTER = /\p{Script=Arabic}/gu
const DEVANAGARI_LETTER = /\p{Script=Devanagari}/gu
const LATIN_LETTER = /\p{Script=Latin}/gu
/** Fewer letters than this say nothing about a language. */
const MIN_LETTERS = 3
/** A script must hold at least this share of the letters. */
const DOMINANT_SHARE = 0.5

const count = (text: string, pattern: RegExp) => text.match(pattern)?.length ?? 0

export type ReplyScript = 'ar' | 'hi' | 'latin'

/**
 * The script a reply is written in: Arabic, Devanagari (Hindi), or Latin (English or French: the preview does
 * not say which). Null when there are too few letters to say, or no script holds half of them. The API does not
 * send the language it detected, so this is what the "What the AI read" card can honestly show.
 */
export function scriptLanguageOf(text: string | null | undefined): ReplyScript | null {
  if (!text) return null
  const clean = text.replace(MARKER, ' ')
  const arabic = count(clean, ARABIC_LETTER)
  const hindi = count(clean, DEVANAGARI_LETTER)
  const latin = count(clean, LATIN_LETTER)
  const letters = arabic + hindi + latin
  if (letters < MIN_LETTERS) return null
  if (arabic / letters >= DOMINANT_SHARE) return 'ar'
  if (hindi / letters >= DOMINANT_SHARE) return 'hi'
  if (latin / letters >= DOMINANT_SHARE) return 'latin'
  return null
}

/** When the person last wrote (an auto-response is not a person). Null when they never did. */
export function latestInboundAt(messages: readonly DeepfakeThreadMessage[]): string | null {
  const theirs = chronological(
    messages.filter((m) => m.direction === 'inbound' && m.role === 'recipient' && !m.isAutoResponse),
    (m) => m.createdAt,
  )
  return theirs.length > 0 ? theirs[theirs.length - 1].createdAt : null
}

/** The redacted preview of the latest thing a PERSON wrote: never an auto-response, never our own message. */
export function latestInboundPreview(messages: readonly DeepfakeThreadMessage[]): string | null {
  const theirs = chronological(
    messages.filter((m) => m.direction === 'inbound' && m.role === 'recipient' && !m.isAutoResponse && m.redactedPreview),
    (m) => m.createdAt,
  )
  return theirs.length > 0 ? theirs[theirs.length - 1].redactedPreview : null
}
